import { CommonModule, Location } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Router } from '@angular/router';
import { GeneralSidebarComponent } from '../../../components/general-sidebar/general-sidebar.component';
import { PageHeaderComponent } from '../../../components/page-header/page-header.component';
import { WizardStepperComponent, WizardStepItem } from '../../../components/wizard-stepper/wizard-stepper.component';
import { BusinessInfoStepComponent } from './steps/business-info-step.component';
import { DirectorsStepComponent } from './steps/directors-step.component';
import { BankDetailsStepComponent } from './steps/bank-details-step.component';
import { DocumentsStepComponent } from './steps/documents-step.component';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import alertify from 'alertifyjs';
import { ApiServiceService } from '../../../modules/services/api-service.service';
import { MdaResponse } from '../../../modules/shared/models/mda.model';
import { AuthenticationService } from '../../../modules/services/authentication.service';
import { HttpParams } from '@angular/common/http';
import { map } from 'rxjs';

@Component({
  selector: 'app-vendor-wizard',
  standalone: true,
  imports: [
    CommonModule,
    PageHeaderComponent,
    WizardStepperComponent,
    ReactiveFormsModule,
    BusinessInfoStepComponent,
    DirectorsStepComponent,
    BankDetailsStepComponent,
    DocumentsStepComponent,
  ],
  templateUrl: './vendor-wizard.component.html',
  styleUrl: './vendor-wizard.component.scss',
})
export class VendorWizardComponent implements OnInit {
  private static readonly REQUIRED_VENDOR_DOCUMENTS = [
    { key: 'companyProfile', label: 'Company Profile', fileTypes: ['pdf'] },
    { key: 'cacCertificate', label: 'CAC Certificate', fileTypes: ['pdf'] },
    { key: 'directorTcc', label: 'Director TCC', fileTypes: ['pdf'] },
    { key: 'directorId', label: 'Director ID', fileTypes: ['jpg', 'jpeg', 'png'] },
    { key: 'citTcc', label: 'CIT TCC', fileTypes: ['pdf'] },
  ] as const;

  readonly steps: WizardStepItem[] = [
    { label: 'Business Info', icon: 'business' },
    { label: 'Directors', icon: 'directors' },
    { label: 'Bank Details', icon: 'bank' },
    { label: 'Documents', icon: 'documents' },
  ];

  activeStep = 0;
  busyAsin = false;
  busyCac = false;
  busyDirectorAsin: Record<number, boolean> = {};
  busyBankVerify = false;
  busyVendorExistenceCheck = false;
  isSubmitting = false;
  isValidatingStep = false;
  mdaList: MdaResponse | null = null;
  categoryList: { data: Array<{ id: string; name: string }> } | null = null;
  banks: Array<{ id: string; code: string; name: string }> = [];
  private banksLoaded = false;
  private pendingBankLabelForEdit: string | null = null;
  private lastAsinValidationData: any | null = null;
  private lastRcValidationData: any | null = null;
  duplicateDirectorModalMessage: string | null = null;
  businessInfoReadOnly = false;
  private myVendors: any[] = [];
  private prefillAttempted = false;
  prefillBusy = false;

  private readonly fb = inject(FormBuilder);

  form: FormGroup;

  location: Location = inject(Location);

  constructor(
    private readonly route: ActivatedRoute,
    private readonly apiService: ApiServiceService,
    private readonly router: Router,
    private readonly authService: AuthenticationService,
  ) {
    alertify.set('notifier', 'position', 'top-right');
    alertify.set('notifier', 'delay', 4);
  }

  ngOnInit(): void {
    this.initForm();
    this.loadMdas();
    this.loadCategories();
    this.loadBanks();

    if (this.isUserVendorCreateRoute) {
      this.loadMyVendorsForUniqueness();
      this.prefillBusinessInfo();
    }

    if (this.mode === 'edit' && this.vendorId) {
      this.relaxDocumentsValidationForEdit();
      this.loadVendorForEdit(this.vendorId);
    }
  }

  initForm() {
    this.form = this.fb.group({
      businessInfo: this.fb.group({
        asin: ['', [Validators.required]],
        vendorName: ['', [Validators.required]],
        phoneNumber: ['', [Validators.required, Validators.pattern(/^\d{10,11}$/)]],
        email: ['', [Validators.required, Validators.email]],
        address: ['', [Validators.required]],
        rcNumber: [
          {value: '', disabled: this.mode === "create"}, 
          [Validators.required]
        ],
        businessClassification: [
          {value: '', disabled: this.mode === "create"}, 
          [Validators.required]
        ],
        dateOfIncorporation: [
          {value: '', disabled: this.mode === "create"}, 
          [Validators.required]
        ],
        tin: [
          {value: '', disabled: this.mode === "create"}, 
          [Validators.required]
        ],
        natureOfService: [
          {value: '', disabled: this.mode === "create"}, 
          [Validators.required]
        ],
        mdaAssociation: ['', [Validators.required]],
        contractCategory: ['', [Validators.required]],
        vendorType: ['', [Validators.required]],
      }),
      directors: this.fb.array<FormGroup>([this.createDirectorGroup()]),
      bankDetails: this.fb.group({
        accountNumber: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]],
        bank: ['', [Validators.required]],
        accountName: [{ value: '', disabled: true }, [Validators.required]],
        bankVerified: [false, [Validators.requiredTrue]],
      }),
      documents: this.fb.array<FormGroup>([
        ...VendorWizardComponent.REQUIRED_VENDOR_DOCUMENTS.map((doc) =>
          this.createDocumentGroup(doc.key, doc.label, [...doc.fileTypes]),
        ),
      ]),
    });

    this.bankDetailsGroup.valueChanges.subscribe({
      next: (values) => {
        // if (!this.bankDetailsGroup.get('bankVerified')?.value) return;
        // this.bankDetailsGroup.patchValue({ bankVerified: false, accountName: '' }, { emitEvent: false });
        const accountNumberControl = this.bankDetailsGroup.get('accountNumber');
        const bankControl = this.bankDetailsGroup.get('bank');

        accountNumberControl.markAsTouched({emitEvent: true});
        bankControl.markAsTouched({emitEvent: true});
        if (accountNumberControl.valid && bankControl.valid) {
          this.verifyBankDetails();
        } else {
          this.bankDetailsGroup.patchValue({ bankVerified: false, accountName: '' }, { emitEvent: false })
        };
      }
    })
  }

  private loadMdas(): void {
    this.apiService.get('/mdas').subscribe({
      next: (res: MdaResponse) => {
        this.mdaList = res;
      },
      error: () => {
        this.mdaList = null;
      },
    });
  }

  private loadCategories(): void {
    this.apiService.get('/categories').subscribe({
      next: (res: any) => {
        const data: Array<any> = Array.isArray(res?.data) ? res.data : [];
        this.categoryList = {
          data: data
            .map((item) => ({
              id: String(item?.id ?? ''),
              name: String(item?.name ?? item?.category_name ?? ''),
            }))
            .filter((item) => item.id && item.name),
        };
      },
      error: () => {
        this.categoryList = null;
      },
    });
  }

  private loadBanks(): void {
    this.apiService.get('/utility/banks').subscribe({
      next: (res: any) => {
        const data: Array<any> = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        this.banks = data
          .map((b) => ({
            id: String(b?.id ?? ''),
            code: String(b?.code ?? '').trim(),
            name: String(b?.name ?? '').trim(),
          }))
          .filter((b) => b.code && b.name);
        this.banksLoaded = true;

        if (this.pendingBankLabelForEdit) {
          const code = this.resolveBankCode(this.pendingBankLabelForEdit);
          if (code) {
            this.bankDetailsGroup.get('bank')?.setValue(code, { emitEvent: false });
          }
          this.pendingBankLabelForEdit = null;
        }
      },
      error: () => {
        this.banks = [];
        this.banksLoaded = true;
      },
    });
  }

  get vendorId(): string | null {
    return this.route.snapshot.paramMap.get('id');
  }

  get mode(): 'create' | 'edit' {
    return this.vendorId ? 'edit' : 'create';
  }

  get showUserBackButton(): boolean {
    return this.isUserVendorCreateRoute;
  }

  private get isUserVendorCreateRoute(): boolean {
    const accountType = String(this.authService.getAccountType() ?? '').toUpperCase();
    if (accountType !== 'USER') return false;
    if (this.mode !== 'create') return false;
    return this.router.url.startsWith('/user/vendors/new');
  }

  get businessInfoGroup(): FormGroup {
    return this.form.get('businessInfo') as FormGroup;
  }

  get directorsArray(): FormArray<FormGroup> {
    return this.form.get('directors') as FormArray<FormGroup>;
  }

  get bankDetailsGroup(): FormGroup {
    return this.form.get('bankDetails') as FormGroup;
  }

  get documentsArray(): FormArray<FormGroup> {
    return this.form.get('documents') as FormArray<FormGroup>;
  }

  get isFirstStep(): boolean {
    return this.activeStep === 0;
  }

  get isLastStep(): boolean {
    return this.activeStep === this.steps.length - 1;
  }

  get canProceedCurrentStep(): boolean {
    if (this.activeStep === 2) return this.bankDetailsGroup.valid;
    return true;
  }

  goBack(): void {
    this.activeStep = Math.max(0, this.activeStep - 1);
  }

  goNext(): void {
    if (!this.validateStep(this.activeStep, true)) {
      this.notifyStepValidationError(this.activeStep);
      return;
    }

    if (this.activeStep === 0) {
      if (this.mode === 'create') {
        const result = this.evaluateBusinessInfoCrossChecks();
        if (!result.ok) {
          alertify.error(result.errors.join(' '));
          return;
        }

        if (this.shouldCheckVendorExistenceOnBusinessInfoNext()) {
          this.checkVendorExistenceAndProceedToNextStep();
          return;
        }
      }
    }

    if (this.activeStep === 1) {
      const duplicateMessage = this.findAnyDirectorDuplicateMessage();
      if (duplicateMessage) {
        this.duplicateDirectorModalMessage = duplicateMessage;
        return;
      }
      this.verifyBankDetails();
    }
    this.activeStep = Math.min(this.steps.length - 1, this.activeStep + 1);
  }

  submit(): void {
    if (!this.validateStep(this.activeStep, true)) return;
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.isSubmitting) return;

    if (this.mode === 'create' && this.shouldCheckVendorExistenceOnSubmit()) {
      this.checkVendorExistenceThenSubmit();
      return;
    }

    this.performSubmit();
  }

  private performSubmit(): void {
    if (this.isUserVendorCreateRoute) {
      const duplicatePairMessage = this.findDuplicateMdaCategoryPairMessage();
      if (duplicatePairMessage) {
        alertify.error(duplicatePairMessage);
        return;
      }
    }

    this.isSubmitting = true;
    if (this.mode === 'edit' && this.vendorId) {
      const payload = this.buildUpdateVendorPayload();
      // eslint-disable-next-line no-console
      console.log('Update vendor payload:', payload);
      this.apiService.put(`/vendors/${this.vendorId}`, payload).subscribe({
        next: (response: any) => {
          alertify.success(response?.message || 'Vendor updated successfully.');
          this.isSubmitting = false;
          const accountType = String(this.authService.getAccountType() ?? '').toUpperCase();
          if (accountType && accountType !== 'USER') {
            void this.router.navigate(['/admin/vendors'], {replaceUrl: true});
          } else {
            void this.router.navigate(['/user/vendors'], {replaceUrl: true});
          }
        },
        error: (error) => {
          const message = typeof error === 'string' ? error : 'Unable to update vendor. Please try again.';
          alertify.error(message);
          this.isSubmitting = false;
        },
      });
      return;
    }

    const payload = this.buildCreateVendorPayload();
    // eslint-disable-next-line no-console
    console.log('Create vendor payload:', payload);
    this.apiService.post('/vendors', payload).subscribe({
      next: (response: any) => {
        alertify.success(response?.message || 'Vendor created successfully.');
        this.isSubmitting = false;
        const accountType = String(this.authService.getAccountType() ?? '').toUpperCase();
        if (accountType && accountType !== 'USER') {
          void this.router.navigate(['/admin/vendors']);
        } else {
          void this.router.navigate(['/user/vendors']);
        }
      },
      error: (error) => {
        const message = typeof error === 'string' ? error : 'Unable to create vendor. Please try again.';
        alertify.error(message);
        this.isSubmitting = false;
      },
    });
  }

  private shouldCheckVendorExistenceOnBusinessInfoNext(): boolean {
    if (this.busyVendorExistenceCheck) return false;
    if (this.mode !== 'create') return false;
    if (this.activeStep !== 0) return false;

    const asin = String(this.businessInfoGroup.get('asin')?.value ?? '').trim();
    const mdaId = String(this.businessInfoGroup.get('mdaAssociation')?.value ?? '').trim();
    if (!asin || !mdaId) return false;

    // Only check if MDA was explicitly chosen/changed by the user.
    return !!this.businessInfoGroup.get('mdaAssociation')?.dirty;
  }

  private shouldCheckVendorExistenceOnSubmit(): boolean {
    if (this.busyVendorExistenceCheck) return false;
    if (this.mode !== 'create') return false;
    const asin = String(this.businessInfoGroup.get('asin')?.value ?? '').trim();
    const mdaId = String(this.businessInfoGroup.get('mdaAssociation')?.value ?? '').trim();
    if (!asin || !mdaId) return false;
    return !!this.businessInfoGroup.get('mdaAssociation')?.dirty;
  }

  private checkVendorExistenceAndProceedToNextStep(): void {
    const asin = String(this.businessInfoGroup.get('asin')?.value ?? '').trim();
    const mdaId = String(this.businessInfoGroup.get('mdaAssociation')?.value ?? '').trim();
    if (!asin || !mdaId) {
      this.activeStep = Math.min(this.steps.length - 1, this.activeStep + 1);
      return;
    }

    this.busyVendorExistenceCheck = true;
    this.checkVendorExistsByAsinMda(asin, mdaId).subscribe({
      next: (exists) => {
        if (exists) {
          alertify.error('A vendor with this ASIN already exists for the selected MDA.');
          this.busyVendorExistenceCheck = false;
          return;
        }

        this.busyVendorExistenceCheck = false;
        this.activeStep = Math.min(this.steps.length - 1, this.activeStep + 1);
      },
      error: (error) => {
        const message = typeof error === 'string' ? error : 'Unable to validate vendor uniqueness. Please try again.';
        alertify.error(message);
        this.busyVendorExistenceCheck = false;
      },
    });
  }

  private checkVendorExistenceThenSubmit(): void {
    const asin = String(this.businessInfoGroup.get('asin')?.value ?? '').trim();
    const mdaId = String(this.businessInfoGroup.get('mdaAssociation')?.value ?? '').trim();
    if (!asin || !mdaId) {
      // No data to check; proceed with submission.
      this.performSubmit();
      return;
    }

    this.busyVendorExistenceCheck = true;
    this.checkVendorExistsByAsinMda(asin, mdaId).subscribe({
      next: (exists) => {
        if (exists) {
          alertify.error('A vendor with this ASIN already exists for the selected MDA.');
          this.busyVendorExistenceCheck = false;
          return;
        }

        this.busyVendorExistenceCheck = false;
        this.performSubmit();
      },
      error: (error) => {
        const message = typeof error === 'string' ? error : 'Unable to validate vendor uniqueness. Please try again.';
        alertify.error(message);
        this.busyVendorExistenceCheck = false;
      },
    });
  }

  private checkVendorExistsByAsinMda(asin: string, mdaId: string) {
    const params = new HttpParams().set('asin', asin).set('mda_id', mdaId);
    return this.apiService.get('/vendors/exists', { params }).pipe(
      map((response: any) => {
        if (response?.status !== 'success') {
          // Backend can still return non-200 statuses; those are handled by ApiServiceService error handler.
          return false;
        }
        return response?.data?.exists === true;
      }),
    );
  }

  goBackToUserDashboard(): void {
    void this.router.navigate(['/user/vendors']);
  }

  private loadMyVendorsForUniqueness(): void {
    this.apiService.getMyVendors().subscribe({
      next: (response: any) => {
        const rows = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
        this.myVendors = rows;
      },
      error: () => {
        this.myVendors = [];
      },
    });
  }

  private prefillBusinessInfo(): void {
    if (this.prefillAttempted) return;
    this.prefillAttempted = true;
    this.prefillBusy = true;

    this.apiService.get('/vendors/mine/prefill').subscribe({
      next: (response: any) => {
        const data = response?.data ?? response;
        this.applyBusinessPrefill(data);
        this.prefillBusy = false;
      },
      error: () => {
        this.prefillBusy = false;
      },
    });
  }

  private applyBusinessPrefill(raw: any): void {
    const source = String(raw?.source ?? '').toLowerCase();
    const group = this.businessInfoGroup;

    const enableIfHasNoValue = (controlName: string, value: any) => {
      const control = this.businessInfoGroup.get(controlName);
      if (!value) control?.enable({ emitEvent: false });
    };

    const patchAndLock = (values: Record<string, any>) => {
      group.patchValue(values, { emitEvent: false });
      const lockFields = [
        'asin',
        'vendorName',
        'phoneNumber',
        'email',
        'address',
        'rcNumber',
        'businessClassification',
        'dateOfIncorporation',
        'tin',
      ];
      for (const key of lockFields) {
        group.get(key)?.disable({ emitEvent: false });
      }
      this.businessInfoReadOnly = true;
    };
    
    [
      ['rcNumber', String(raw?.vendor?.rc_number ?? '').trim()],
      ['businessClassification', String(raw?.vendor?.business_classification ?? '').trim()], 
      ['dateOfIncorporation', String(raw?.vendor?.date_of_incorporation ?? '').trim()],
      ['tin', String(raw?.vendor?.asin ?? '').trim()],
      ['natureOfService', String(raw?.vendor?.nature_of_service ?? '').trim()]
    ].forEach(([control, result]) => {
      enableIfHasNoValue(control, result);
    });

    if (source === 'existing_vendor') {
      const vendor = raw?.vendor ?? {};
      const asin = String(vendor?.asin ?? '').trim();
      const vendorName = String(vendor?.name ?? '').trim();
      const rcNumber = String(vendor?.rc_number ?? '').trim();

      patchAndLock({
        asin,
        vendorName,
        phoneNumber: String(vendor?.phone_no ?? '').trim(),
        email: String(vendor?.email ?? '').trim(),
        address: String(vendor?.address ?? '').trim(),
        rcNumber,
        businessClassification: String(vendor?.business_classification ?? '').trim(),
        dateOfIncorporation: String(vendor?.date_of_incorporation ?? '').trim(),
        tin: asin,
        natureOfService: String(vendor?.nature_of_service ?? '').trim(),
      });

      this.lastAsinValidationData = {
        rcNumber,
        firstName: vendorName,
        tinNumber: asin,
      };
      this.lastRcValidationData = {
        rcNumber,
        entityName: vendorName,
      };
      return;
    }

    if (source === 'asin_validation') {
      const asinValidation = raw?.asin_validation_details ?? {};
      const business = raw?.business ?? {};

      const rcNumber = String(raw?.rcNumber ?? asinValidation?.rcNumber ?? asinValidation?.rc_number ?? '').trim();
      const tinNumber = String(asinValidation?.tinNumber ?? asinValidation?.tin_number ?? '').trim();
      const asin = tinNumber;
      const vendorName = String(asinValidation?.firstName ?? '').trim();

      const registrationDate = business?.registrationDate ?? business?.registration_date;
      const dateOfIncorporation = this.toIsoDate(registrationDate);

      const lineOfBusiness = Array.isArray(business?.lineOfBusiness)
        ? String(business.lineOfBusiness[0] ?? '').trim()
        : String(business?.lineOfBusiness ?? business?.line_of_business ?? '').trim();

      patchAndLock({
        asin,
        vendorName,
        phoneNumber: String(asinValidation?.phoneNumber ?? asinValidation?.phone ?? '').trim(),
        email: String(asinValidation?.email ?? '').trim(),
        address: String(asinValidation?.address ?? '').trim(),
        rcNumber,
        businessClassification: String(business?.entityType ?? business?.entity_type ?? '').trim(),
        dateOfIncorporation,
        tin: tinNumber,
        natureOfService: lineOfBusiness,
      });

      this.lastAsinValidationData = {
        rcNumber,
        firstName: vendorName,
        tinNumber,
      };
      this.lastRcValidationData = {
        rcNumber,
        entityName: vendorName,
        registrationDate,
        entityType: business?.entityType ?? business?.entity_type,
        lineOfBusiness: business?.lineOfBusiness ?? business?.line_of_business,
      };
    }
  }

  private toIsoDate(value: any): string {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  private findDuplicateMdaCategoryPairMessage(): string | null {
    const raw = this.form.getRawValue() as any;
    const businessInfo = raw.businessInfo ?? {};
    const mdaId = String(businessInfo.mdaAssociation ?? '').trim();
    const categoryId = String(businessInfo.contractCategory ?? '').trim();
    if (!mdaId || !categoryId) return null;

    const hasDuplicate = (this.myVendors ?? []).some((v: any) => {
      return String(v?.mda_id ?? '').trim() === mdaId && String(v?.category_id ?? '').trim() === categoryId;
    });

    if (!hasDuplicate) return null;
    return 'You already have an application for this MDA and Category. Please choose a different combination.';
  }

  addDirector(): void {
    const duplicateMessage = this.findAnyDirectorDuplicateMessage();
    if (duplicateMessage) {
      this.duplicateDirectorModalMessage = duplicateMessage;
      return;
    }
    this.directorsArray.push(this.createDirectorGroup());
  }

  removeDirector(index: number): void {
    if (this.directorsArray.length === 1) return;
    this.directorsArray.removeAt(index);
  }

  verifyDirectorAsin(index: number): void {
    const group = this.directorsArray.at(index);
    if (!group || this.busyDirectorAsin[index]) return;

    const asin = String(group.get('asin')?.value ?? '').trim();
    if (!asin) {
      group.get('asin')?.markAsTouched();
      return;
    }

    const duplicateMessage = this.findDirectorDuplicateMessage(index);
    if (duplicateMessage) {
      this.duplicateDirectorModalMessage = duplicateMessage;
      return;
    }

    this.busyDirectorAsin[index] = true;
    this.apiService.validateAsin(asin).subscribe({
      next: (response: any) => {
        const data = response?.data;
        if (response?.status !== 'success' || !data) {
          alertify.error(response?.message || 'We could not validate this ASIN.');
          this.busyDirectorAsin[index] = false;
          return;
        }

        const rawTaxGroup = String(data.taxGroup ?? data.taxgroup ?? '').trim().toUpperCase();
        if (rawTaxGroup !== 'USER') {
          alertify.error('Director ASIN must be of the group USER, not Otherwise');
          this.busyDirectorAsin[index] = false;
          return;
        }

        const fullName = [data.firstName, data.middleName, data.lastName].filter(Boolean).join(' ');
        group.patchValue({
          fullName: fullName || group.get('fullName')?.value || '',
          email: data.email || group.get('email')?.value || '',
          phoneNumber: data.phone || group.get('phoneNumber')?.value || '',
          address: data.address || group.get('address')?.value || '',
          asinVerified: true,
        });

        if (fullName) group.get('fullName')?.disable({ emitEvent: false });
        if (data.email) group.get('email')?.disable({ emitEvent: false });
        if (data.phone) group.get('phoneNumber')?.disable({ emitEvent: false });
        if (data.address) group.get('address')?.disable({ emitEvent: false });

        alertify.success(response?.message || 'ASIN verified successfully.');
        this.busyDirectorAsin[index] = false;
      },
      error: (error) => {
        const message =
          typeof error === 'string' ? error : 'We could not validate this ASIN. Please try again.';
        alertify.error(message);
        this.busyDirectorAsin[index] = false;
      },
    });
  }

  verifyBankDetails(): void {
    if (this.busyBankVerify) return;
    const accountNumber = String(this.bankDetailsGroup.get('accountNumber')?.value ?? '').trim();
    const bank = String(this.bankDetailsGroup.get('bank')?.value ?? '').trim();

    if (!accountNumber) this.bankDetailsGroup.get('accountNumber')?.markAsTouched();
    if (!bank) this.bankDetailsGroup.get('bank')?.markAsTouched();
    if (this.bankDetailsGroup.get('accountNumber')?.invalid || this.bankDetailsGroup.get('bank')?.invalid) {
      return;
    }

    this.busyBankVerify = true;

    const accountNumberControl = this.bankDetailsGroup.get('accountNumber');
    const bankControl = this.bankDetailsGroup.get('bank');

    accountNumberControl.disable({emitEvent: false});
    bankControl.disable({emitEvent: false});

    const params = new HttpParams()
      .set('account_number', accountNumber)
      .set('bank_code', bank);

    this.apiService.get('/utility/banks/resolve', { params }).subscribe({
      next: (response: any) => {
        if (response?.status !== 'success') {
          const message = String(response?.message ?? 'failed to resolve account number');
          this.bankDetailsGroup.patchValue({ accountName: '', bankVerified: false }, { emitEvent: false });
          this.bankDetailsGroup.get('accountName')?.disable({ emitEvent: false });
          this.bankDetailsGroup.get('bankVerified')?.markAsDirty();
          alertify.error(message);
          this.busyBankVerify = false;
          return;
        }

        const accountName = String(response?.data?.account_name ?? '').trim();
        this.bankDetailsGroup.patchValue(
          {
            accountName,
            bankVerified: true,
          },
          { emitEvent: false },
        );
        this.bankDetailsGroup.get('accountName')?.disable({ emitEvent: false });
        this.bankDetailsGroup.get('bankVerified')?.markAsDirty();
        alertify.success(String(response?.message ?? 'Account number resolved'));
        this.busyBankVerify = false;
        accountNumberControl.enable({emitEvent: false});
        bankControl.enable({emitEvent: false});
      },
      error: (error) => {
        const message =
          typeof error === 'string'
            ? error
            : 'failed to resolve account number';
        this.bankDetailsGroup.patchValue({ accountName: '', bankVerified: false }, { emitEvent: false });
        this.bankDetailsGroup.get('accountName')?.disable({ emitEvent: false });
        this.bankDetailsGroup.get('bankVerified')?.markAsDirty();
        alertify.error(message);
        this.busyBankVerify = false;
      },
    });
  }

  closeDuplicateDirectorModal(): void {
    this.duplicateDirectorModalMessage = null;
  }

  onDocumentFileSelected(event: { index: number; file: File }): void {
    const group = this.documentsArray.at(event.index);
    if (!group) return;
    const maxSizeInBytes = 2 * 1024 * 1024;
    if (event.file.size > maxSizeInBytes) {
      alertify.error(
        `File is too large. Maximum size is 2MB. Your file is ${(event.file.size / (1024 * 1024)).toFixed(2)}MB.`,
      );
      return;
    }

    const incomingFileName = String(event.file.name ?? '').trim();
    if (incomingFileName) {
      const incomingKey = incomingFileName.toLowerCase();
      const duplicateIndex = this.documentsArray.controls.findIndex((doc, idx) => {
        if (idx === event.index) return false; // allow re-selecting the same file for the same slot
        const existingName =
          String(doc.get('fileName')?.value ?? doc.get('file')?.value?.name ?? '').trim();
        if (!existingName) return false;
        return existingName.toLowerCase() === incomingKey;
      });

      if (duplicateIndex !== -1) {
        const duplicateLabel = String(
          this.documentsArray.at(duplicateIndex)?.get('label')?.value ?? '',
        ).trim();
        alertify.error(
          duplicateLabel
            ? `This file has already been selected for "${duplicateLabel}". Please choose a different document.`
            : 'This file has already been selected for another requirement. Please choose a different document.',
        );
        return;
      }
    }

    void this.toBase64(event.file)
      .then((dataUrl) => {
        const base64 = this.extractBase64(dataUrl);
        if (!base64) {
          alertify.error('Unable to process file. Please try another file.');
          return;
        }
        group.patchValue({
          file: event.file,
          fileName: event.file.name,
          documentBase64: base64,
        });
        group.markAsDirty();
      })
      .catch(() => {
        alertify.error('Unable to process file. Please try another file.');
      });
  }

  removeDocumentFile(index: number): void {
    const group = this.documentsArray.at(index);
    if (!group) return;
    group.patchValue({ file: null, fileName: '', documentBase64: '' });
    group.markAsDirty();
  }

  validateAsin(): void {
    if (this.busyAsin) return;
    const asinControl = this.businessInfoGroup.get('asin');
    const asin = String(asinControl?.value ?? '').trim();
    if (!asin) {
      asinControl?.markAsTouched();
      return;
    }
    this.busyAsin = true;

    this.apiService.validateAsinWithDetails(asin).subscribe({
      next: (response: any) => {
        const data = response?.data;
        if (response?.status !== 'success' || !data) {
          alertify.error(response?.message || 'We could not validate this ASIN.');
          this.busyAsin = false;
          return;
        }

        const rawTaxGroup = String(data.taxGroup ?? data.taxgroup ?? '').toUpperCase();
        if (rawTaxGroup === 'USER') {
          alertify.error('USER ASIN is not allowed. Please provide a COMPANY ASIN.');
          this.busyAsin = false;
          return;
        }

        if (rawTaxGroup && rawTaxGroup !== 'COMPANY') {
          alertify.error('We could not validate this ASIN. Invalid tax group.');
          this.busyAsin = false;
          return;
        }

        this.lastAsinValidationData = data;

        const vendorName = [data.firstName, data.middleName, data.lastName].filter(Boolean).join(' ');

        if (data.asin) {
          this.businessInfoGroup.get('asin')?.setValue(data.asin, { emitEvent: false });
        }

        if (data.rcNumber) {
          this.businessInfoGroup.get('rcNumber')?.setValue(data.rcNumber, { emitEvent: false });
        }

        if (vendorName) {
          this.businessInfoGroup.get('vendorName')?.setValue(vendorName, { emitEvent: false });
          this.businessInfoGroup.get('vendorName')?.disable({ emitEvent: false });
        }

        if (data.phone || data.phoneNumber) {
          this.businessInfoGroup
            .get('phoneNumber')
            ?.setValue(data.phone || data.phoneNumber, { emitEvent: false });
          this.businessInfoGroup.get('phoneNumber')?.disable({ emitEvent: false });
        }

        if (data.email) {
          const emailControl = this.businessInfoGroup.get('email');
          emailControl?.setValue(data.email, { emitEvent: false });
          emailControl?.updateValueAndValidity({ emitEvent: false });

          if (this.mode === 'create' && emailControl?.invalid) {
            emailControl.enable({ emitEvent: false });
            emailControl.markAsTouched();
          } else {
            emailControl?.disable({ emitEvent: false });
          }
        }

        if (data.address) {
          this.businessInfoGroup.get('address')?.setValue(data.address, { emitEvent: false });
          this.businessInfoGroup.get('address')?.disable({ emitEvent: false });
        }

        const tinNumber = data.tinNumber ?? data.tin_number ?? data.tin;
        if (tinNumber) {
          this.businessInfoGroup.get('tin')?.setValue(String(tinNumber), { emitEvent: false });
          this.businessInfoGroup.get('tin')?.disable({ emitEvent: false });
        }

        // Enable 'CAC Verification' fields when the ASIN has been validated
        if (this.mode === "create") {
          this.enableCacVerificationControls();
        }

        alertify.success(response?.message || 'ASIN validated successfully.');
        this.busyAsin = false;
      },
      error: (error) => {
        const message =
          typeof error === 'string'
            ? error
            : 'We could not validate this ASIN. Please try again.';
        alertify.error(message);
        this.busyAsin = false;
      },
    });
  }

  verifyCac(): void {
    if (this.busyCac) return;
    const rcNumber = String(this.businessInfoGroup.get('rcNumber')?.value ?? '').trim();
    const entityName = String(this.businessInfoGroup.get('vendorName')?.value ?? '').trim();
    if (!rcNumber) {
      this.businessInfoGroup.get('rcNumber')?.markAsTouched();
      return;
    }
    this.busyCac = true;

    this.apiService.validateBusinessRc(rcNumber, entityName).subscribe({
      next: (response: any) => {
        const data = response?.data;
        if (response?.status !== 'success' || !data) {
          alertify.error(response?.message || 'We could not validate this RC number.');
          this.busyCac = false;
          return;
        }

        this.lastRcValidationData = data;

        const registrationDate = data.registrationDate ? new Date(data.registrationDate) : null;
        const dateOfIncorporation =
          registrationDate && !Number.isNaN(registrationDate.getTime())
            ? `${registrationDate.getFullYear()}-${String(registrationDate.getMonth() + 1).padStart(
                2,
                '0',
              )}-${String(registrationDate.getDate()).padStart(2, '0')}`
            : '';

        this.businessInfoGroup.patchValue({
          businessClassification:
            this.businessInfoGroup.get('businessClassification')?.value || data.entityType || '',
          dateOfIncorporation:
            this.businessInfoGroup.get('dateOfIncorporation')?.value ||
            dateOfIncorporation ||
            '',
        });

        if (data.rcNumber) {
          this.businessInfoGroup.get('rcNumber')?.setValue(data.rcNumber, { emitEvent: false });
          this.businessInfoGroup.get('rcNumber')?.disable({emitEvent: false});
        }

        if (data.entityType) {
          this.businessInfoGroup.get('businessClassification')?.disable({ emitEvent: false });
        }

        if (dateOfIncorporation) {
          this.businessInfoGroup.get('dateOfIncorporation')?.disable({ emitEvent: false });
        }

        this.businessInfoGroup.get('tin')?.disable({emitEvent: false});

        const lineOfBusiness = String(data.lineOfBusiness ?? data.line_of_business ?? '').trim();
        if (lineOfBusiness) {
          this.businessInfoGroup.get('natureOfService')?.setValue(lineOfBusiness, { emitEvent: false });
          this.businessInfoGroup.get('natureOfService')?.disable({ emitEvent: false });
        } else {
          this.businessInfoGroup.get('natureOfService')?.enable({ emitEvent: false });
        }

        alertify.success(response?.message || 'Business RC number validated successfully.');
        this.busyCac = false;
      },
      error: (error) => {
        const message = 'We could not validate this RC number. Please try again.';
        alertify.error(message);
        this.busyCac = false;
      },
    });
  }

  private evaluateBusinessInfoCrossChecks(): { ok: boolean; errors: string[] } {
    const errors: string[] = [];
    if (!this.lastAsinValidationData) errors.push('Please validate ASIN before continuing.');
    if (!this.lastRcValidationData) errors.push('Please validate RC number before continuing.');
    if (errors.length) return { ok: false, errors };

    const asin = this.lastAsinValidationData as any;
    const rc = this.lastRcValidationData as any;

    const normalize = (value: unknown) =>
      String(value ?? '')
        .trim()
        .replace(/\s+/g, ' ')
        .toLowerCase();

    // const asinAddress = normalize(asin.address);
    // const rcAddress = normalize(rc.address);
    // if (asinAddress && rcAddress && asinAddress !== rcAddress) {
    //   errors.push('ASIN address and RC address do not match.');
    // }

    const asinRc = normalize(asin.rcNumber ?? asin.rc_number);
    const rcRc = normalize(rc.rcNumber ?? rc.rc_number);
    if (asinRc && rcRc && asinRc !== rcRc) {
      errors.push('RC number from ASIN validation does not match RC validation.');
    }

    const asinName = normalize(asin.firstName);
    const rcName = normalize(rc.entityName);
    if (asinName && rcName && asinName !== rcName) {
      errors.push('Company name from ASIN validation does not match RC entity name.');
    }

    return { ok: errors.length === 0, errors };
  }

  private buildCreateVendorPayload(): any {
    const raw = this.form.getRawValue() as any;
    const businessInfo = raw.businessInfo ?? {};
    const bankDetails = raw.bankDetails ?? {};

    const asinData = this.lastAsinValidationData ?? {};
    const vendorName = String(businessInfo.vendorName ?? '').trim();

    const firstName =
      String(asinData.firstName ?? asinData.first_name ?? '').trim() ||
      vendorName.split(' ').slice(0, 1).join(' ');
    const middleName = String(asinData.middleName ?? asinData.middle_name ?? '').trim();
    const lastName =
      String(asinData.lastName ?? asinData.last_name ?? '').trim() ||
      vendorName.split(' ').slice(1).join(' ');

    const documents = (raw.documents ?? [])
      .map((doc: any) => ({
        document_name: String(doc.label ?? '').trim(),
        document_base64: String(doc.documentBase64 ?? '').trim(),
      }))
      .filter((doc: any) => doc.document_name && doc.document_base64);

    const directors = (raw.directors ?? [])
      .map((d: any) => ({
        asin: String(d.asin ?? '').trim(),
        email: String(d.email ?? '').trim(),
        name: String(d.fullName ?? '').trim(),
        address: String(d.address ?? '').trim(),
        bvn: String(d.bvn ?? '').trim(),
        phone_number: String(d.phoneNumber ?? '').trim(),
      }))
      .filter((d: any) => d.asin && d.email && d.name);

    return {
      name: vendorName,
      email: String(businessInfo.email ?? '').trim(),
      first_name: firstName,
      last_name: lastName,
      middle_name: middleName,
      phone_number: String(businessInfo.phoneNumber ?? '').trim(),
      mda_id: String(businessInfo.mdaAssociation ?? '').trim(),
      category_id: String(businessInfo.contractCategory ?? '').trim(),
      vendor_type: String(businessInfo.vendorType ?? '').trim(),
      asin: String(businessInfo.asin ?? '').trim(),
      address: String(businessInfo.address ?? '').trim(),
      business_classification: String(businessInfo.businessClassification ?? '').trim(),
      nature_of_service: String(businessInfo.natureOfService ?? '').trim(),
      rc_number: String(businessInfo.rcNumber ?? '').trim(),
      date_of_incorporation: String(businessInfo.dateOfIncorporation ?? '').trim(),
      account: {
        account_number: String(bankDetails.accountNumber ?? '').trim(),
        asin: String(businessInfo.asin ?? '').trim(),
        account_name: String(bankDetails.accountName ?? '').trim(),
        bank: String(bankDetails.bank ?? '').trim(),
      },
      documents,
      directors,
    };
  }

  private validateStep(stepIndex: number, markTouched = false): boolean {
    this.isValidatingStep = true;
    const group = this.getStepGroup(stepIndex);
    if (!group) return true;
    if (markTouched) group.markAllAsTouched();
    this.isValidatingStep = false;
    return group.valid;
  }

  private notifyStepValidationError(stepIndex: number): void {
    if (stepIndex === 0) {
      const missing: string[] = [];
      const email = this.businessInfoGroup.get('email');
      const phone = this.businessInfoGroup.get('phoneNumber');

      const requiredFields: Array<[string, string]> = [
        ['asin', 'ASIN'],
        ['vendorName', 'Vendor name'],
        ['phoneNumber', 'Phone number'],
        ['email', 'Email'],
        ['address', 'Address'],
        ['rcNumber', 'RC number'],
        ['businessClassification', 'Business classification'],
        ['dateOfIncorporation', 'Date of incorporation'],
        ['natureOfService', 'Nature of service'],
        ['mdaAssociation', 'MDA association'],
        ['contractCategory', 'Contract category'],
        ['vendorType', 'Vendor type'],
      ];

      for (const [key, label] of requiredFields) {
        const control = this.businessInfoGroup.get(key);
        if (control?.invalid) missing.push(label);
      }

      const details: string[] = [];
      if (email?.errors?.['email']) details.push('Enter a valid email address.');
      if (phone?.errors?.['pattern']) details.push('Phone number must be 10 or 11 digits.');

      alertify.error(
        [
          'Please complete the Business Info step.',
          missing.length ? `Missing/invalid: ${missing.join(', ')}.` : '',
          details.length ? details.join(' ') : '',
        ]
          .filter(Boolean)
          .join(' '),
      );
      return;
    }

    if (stepIndex === 1) {
      alertify.error(
        'Please add at least one director and ensure all director fields are valid and ASIN is verified.',
      );
      return;
    }

    if (stepIndex === 2) {
      alertify.error(
        'Please enter a valid account number, select a bank, and verify the bank details before continuing.',
      );
      return;
    }

    if (stepIndex === 3) {
      const missingDocs = this.documentsArray.controls
        .filter((g) => g.get('file')?.invalid)
        .map((g) => String(g.get('label')?.value ?? 'Document'))
        .filter(Boolean);

      alertify.error(
        missingDocs.length
          ? `Please upload all required documents. Missing: ${missingDocs.join(', ')}.`
          : 'Please upload all required documents before submitting.',
      );
    }
  }

  private getStepGroup(stepIndex: number): FormGroup | FormArray<FormGroup> | null {
    if (stepIndex === 0) return this.businessInfoGroup;
    if (stepIndex === 1) return this.directorsArray;
    if (stepIndex === 2) return this.bankDetailsGroup;
    if (stepIndex === 3) return this.documentsArray;
    return null;
  }

  private createDirectorGroup(): FormGroup {
    const group = this.fb.group({
      asin: ['', [Validators.required]],
      asinVerified: [false, [Validators.requiredTrue]],
      fullName: ['', [Validators.required]],
      phoneNumber: ['', [Validators.required, Validators.pattern(/^\d{10,11}$/)]],
      email: ['', [Validators.required, Validators.email]],
      address: ['', [Validators.required]],
      bvn: ['', [Validators.required, Validators.pattern(/^\d{11}$/)]],
    });

    group.get('asin')?.valueChanges.subscribe(() => {
      if (!group.get('asinVerified')?.value) return;
      group.patchValue(
        {
          asinVerified: false,
          fullName: '',
          phoneNumber: '',
          email: '',
          address: '',
        },
        { emitEvent: false },
      );
      group.get('fullName')?.enable({ emitEvent: false });
      group.get('phoneNumber')?.enable({ emitEvent: false });
      group.get('email')?.enable({ emitEvent: false });
      group.get('address')?.enable({ emitEvent: false });
    });

    return group;
  }

  private buildUpdateVendorPayload(): any {
    return this.buildCreateVendorPayload();
  }

  private loadVendorForEdit(vendorId: string): void {
    this.apiService.get(`/vendors/${vendorId}`).subscribe({
      next: (response: any) => {
        const vendor = response?.data;
        if (response?.status !== 'success' || !vendor) {
          alertify.error(response?.message || 'Unable to fetch vendor details.');
          return;
        }

        const tinFromApi = String(vendor.tin ?? vendor.tin_number ?? vendor.tinNumber ?? '').trim();
        const tinValue = tinFromApi || String(vendor.asin ?? '').trim();

        this.businessInfoGroup.patchValue({
          asin: String(vendor.asin ?? ''),
          vendorName: String(vendor.name ?? ''),
          phoneNumber: String(vendor.phone_no ?? vendor.phone_number ?? ''),
          email: String(vendor.email ?? ''),
          address: String(vendor.address ?? ''),
          rcNumber: String(vendor.rc_number ?? ''),
          businessClassification: String(vendor.business_classification ?? ''),
          dateOfIncorporation: String(vendor.date_of_incorporation ?? ''),
          tin: tinValue,
          natureOfService: String(vendor.nature_of_service ?? ''),
          mdaAssociation: String(vendor.mda_id ?? ''),
          contractCategory: String(vendor.category_id ?? ''),
          vendorType: String((vendor as any).vendor_type ?? ''),
        }, { emitEvent: false });

        const disableIfHasValue = (controlName: string) => {
          const control = this.businessInfoGroup.get(controlName);
          const value = String(control?.value ?? '').trim();
          if (value) control?.disable({ emitEvent: false });
        };

        // Edit mode: lock down vendor identity fields if they already exist.
        this.businessInfoGroup.get('asin')?.disable({ emitEvent: false });
        this.businessInfoGroup.get('rcNumber')?.disable({ emitEvent: false });
        disableIfHasValue('vendorName');
        disableIfHasValue('phoneNumber');
        disableIfHasValue('email');
        disableIfHasValue('address');
        disableIfHasValue('businessClassification');
        disableIfHasValue('dateOfIncorporation');
        disableIfHasValue('tin');
        disableIfHasValue('natureOfService');
        disableIfHasValue('mdaAssociation');
        this.businessInfoGroup.get('vendorType')?.enable({ emitEvent: false });

        const directors = Array.isArray(vendor.directors) ? vendor.directors : [];
        this.directorsArray.clear();
        if (!directors.length) {
          this.directorsArray.push(this.createDirectorGroup());
        } else {
          for (const director of directors) {
            const group = this.createDirectorGroup();
            group.patchValue({
              asin: String(director?.asin ?? ''),
              asinVerified: true,
              fullName: String(director?.name ?? ''),
              phoneNumber: String(director?.phone_number ?? ''),
              email: String(director?.email ?? ''),
              address: String(director?.address ?? ''),
              bvn: String(director?.bvn ?? ''),
            });
            const disableDirectorIfHasValue = (controlName: string) => {
              const control = group.get(controlName);
              const value = String(control?.value ?? '').trim();
              if (value) control?.disable({ emitEvent: false });
            };

            // Edit mode: lock director fields that were loaded (except ASIN + BVN).
            disableDirectorIfHasValue('fullName');
            disableDirectorIfHasValue('phoneNumber');
            disableDirectorIfHasValue('email');
            disableDirectorIfHasValue('address');
            this.directorsArray.push(group);
          }
        }

        const account = vendor.account ?? null;
        if (account) {
          const bankValue = String(account.bank ?? '').trim();
          this.bankDetailsGroup.patchValue({
            accountNumber: String(account.account_number ?? ''),
            bank: '',
            accountName: String(account.account_name ?? ''),
            bankVerified: true,
          });
          const bankCode = this.banksLoaded ? this.resolveBankCode(bankValue) : '';
          if (bankCode) {
            this.bankDetailsGroup.get('bank')?.setValue(bankCode, { emitEvent: false });
          } else if (!this.banksLoaded && bankValue) {
            this.pendingBankLabelForEdit = bankValue;
          }
          this.bankDetailsGroup.get('accountName')?.disable({ emitEvent: false });
        }

        const documents = Array.isArray(vendor.documents) ? vendor.documents : [];
        this.patchDocumentsFromVendor(documents);
      },
      error: (error) => {
        const message = typeof error === 'string' ? error : 'Unable to fetch vendor details. Please try again.';
        alertify.error(message);
      },
    });
  }

  private relaxDocumentsValidationForEdit(): void {
    this.documentsArray.controls.forEach((doc) => {
      doc.get('file')?.clearValidators();
      doc.get('documentBase64')?.clearValidators();
      doc.get('file')?.updateValueAndValidity({ emitEvent: false });
      doc.get('documentBase64')?.updateValueAndValidity({ emitEvent: false });
    });
  }

  private patchDocumentsFromVendor(documents: any[]): void {
    const normalized: Array<{ name: string; url: string }> = documents
      .map((doc) => ({
        name: String(doc?.document_name ?? '').trim(),
        url: String(doc?.uploaded_document_url ?? '').trim(),
      }))
      .filter((doc) => doc.name);

    const nameToUrl = new Map<string, string>();
    for (const doc of normalized) {
      if (doc.name && doc.url) nameToUrl.set(doc.name.toLowerCase(), doc.url);
    }

    this.documentsArray.controls.forEach((docGroup) => {
      const label = String(docGroup.get('label')?.value ?? '').trim();
      const url = nameToUrl.get(label.toLowerCase()) ?? '';
      const fileName = url ? this.extractFileNameFromUrl(url) : '';
      docGroup.patchValue({ fileName }, { emitEvent: false });
    });
  }

  private extractFileNameFromUrl(url: string): string {
    try {
      const pathname = new URL(url).pathname;
      const parts = pathname.split('/').filter(Boolean);
      return parts.length ? decodeURIComponent(parts[parts.length - 1] ?? '') : '';
    } catch {
      const cleaned = url.split('?')[0] ?? '';
      const parts = cleaned.split('/').filter(Boolean);
      return parts.length ? parts[parts.length - 1] ?? '' : '';
    }
  }

  private resolveBankCode(bankValue: string): string {
    const raw = String(bankValue ?? '').trim();
    if (!raw) return '';

    // If backend already returns the bank code, accept it directly.
    const exactCode = this.banks.find((b) => String(b.code).trim() === raw);
    if (exactCode?.code) return exactCode.code;

    // Otherwise treat it as a bank name and map to code.
    const normalized = raw.toLowerCase();
    const exactName = this.banks.find((b) => b.name.trim().toLowerCase() === normalized);
    if (exactName?.code) return exactName.code;

    const partial = this.banks.find((b) => b.name.trim().toLowerCase().includes(normalized));
    if (partial?.code) return partial.code;

    return '';
  }

  private findAnyDirectorDuplicateMessage(): string | null {
    for (let i = 0; i < this.directorsArray.length; i++) {
      const message = this.findDirectorDuplicateMessage(i);
      if (message) return message;
    }
    return null;
  }

  private findDirectorDuplicateMessage(index: number): string | null {
    const current = this.directorsArray.at(index);
    if (!current) return null;

    const normalize = (value: unknown) =>
      String(value ?? '')
        .trim()
        .replace(/\s+/g, ' ')
        .toLowerCase();

    const currentAsin = normalize(current.get('asin')?.value);
    const currentEmail = normalize(current.get('email')?.value);
    const currentBvn = normalize(current.get('bvn')?.value);
    const currentPhone = normalize(current.get('phoneNumber')?.value);

    if (!currentAsin && !currentEmail && !currentBvn && !currentPhone) return null;

    for (let i = 0; i < this.directorsArray.length; i++) {
      if (i === index) continue;
      const other = this.directorsArray.at(i);
      if (!other) continue;

      const otherAsin = normalize(other.get('asin')?.value);
      const otherEmail = normalize(other.get('email')?.value);
      const otherBvn = normalize(other.get('bvn')?.value);
      const otherPhone = normalize(other.get('phoneNumber')?.value);

      const duplicates: string[] = [];
      if (currentAsin && otherAsin && currentAsin === otherAsin) duplicates.push('ASIN');
      if (currentEmail && otherEmail && currentEmail === otherEmail) duplicates.push('Email');
      if (currentBvn && otherBvn && currentBvn === otherBvn) duplicates.push('BVN');
      if (currentPhone && otherPhone && currentPhone === otherPhone) duplicates.push('Phone');

      if (duplicates.length) {
        return `Director ${index + 1} duplicates Director ${i + 1} by: ${duplicates.join(', ')}. Please correct the duplicate value(s).`;
      }
    }

    return null;
  }

  private createDocumentGroup(key: string, label: string, fileTypes: string[]): FormGroup {
    const accept = fileTypes
      .map((t) => `.${String(t).trim().replace(/^\./, '')}`)
      .filter(Boolean)
      .join(',');
    return this.fb.group({
      key: [key],
      label: [label],
      fileTypes: [fileTypes],
      accept: [accept],
      fileName: [''],
      file: [null as File | null, [Validators.required]],
      documentBase64: ['', [Validators.required]],
    });
  }

  private toBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(String(reader.result ?? ''));
      reader.onerror = reject;
    });
  }

  private extractBase64(dataUrl: string): string {
    const idx = dataUrl.indexOf(',');
    if (idx === -1) return '';
    return dataUrl.slice(idx + 1).trim();
  }

  private enableCacVerificationControls() {
    [
      'rcNumber',
      'businessClassification', 
      'dateOfIncorporation',
      'tin',
      'natureOfService'
    ].forEach(x => {
      this.form.get(['businessInfo', x]).enable();
    })
  }
}
